import {Box, Grid, Typography} from '@mui/material';
import {Form, Formik, FormikConfig, FormikProps} from 'formik';
import {FC} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useAppDispatch} from 'src/app/hooks';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {AMPInput} from 'src/components/common/AMPInput';
import {AMPPhoneNumber} from 'src/components/common/AMPPhoneNumber';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {getHelperText} from 'src/utils/forms';
import {managersInitialValue, titles} from '../formData/common/initialData';
import {actionsWrapperSx, formSx} from './ManagersForm.sx';
import {ManagersFormProps} from './ManagersForm.types';
import {ManagerFormProps, managersEditFormSchema, managersFormSchema} from '../formData/managers';
import {usersApi} from 'src/api';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch} from 'src/types';
import {isErrorObject, processedLanguages} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {LanguageEnum, TitleEnum, UserTypeEnum} from 'src/enums/common';

type AddManagerMutationTrigger = ReturnType<typeof usersApi.useCreateManagerMutation>[0];
type EditManagerMutationTrigger = ReturnType<typeof usersApi.useUpdateManagerMutation>[0];

const handleSubmit =
    (
        trigger: AddManagerMutationTrigger,
        dispatch: AppDispatch,
        handleClose: () => void,
        businessId: number
    ): FormikConfig<ManagerFormProps>['onSubmit'] =>
        async (values) => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({data: values, businessId}).unwrap();
                handleClose();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

const handleUpdate =
    (
        trigger: EditManagerMutationTrigger,
        dispatch: AppDispatch,
        handleClose: () => void,
        businessId: number,
        userId: number
    ): FormikConfig<ManagerFormProps>['onSubmit'] =>
        async (values) => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({
                    userId,
                    data: {
                        businessId,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        language: values.language as LanguageEnum,
                        title: values.title as TitleEnum,
                        phoneNumber: values.phoneNumber,
                    },
                }).unwrap();
                handleClose();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

/**
 * The kinds of back-office person this form can create - and only create.
 *
 * Which kind they are decides the one role they start with, so it is asked once, at creation. The
 * editor does not offer it: moving somebody between kinds rebound their roles behind the editor's
 * back, and permissions are granted and withdrawn on the access-control screen, one at a time.
 */
const MANAGER_TYPES = [
    UserTypeEnum.CLIENT_MANAGER,
    UserTypeEnum.SALES_MANAGER,
    UserTypeEnum.TECHNICAL_MANAGER,
];

const useAssignableTypes = () =>
    MANAGER_TYPES.map((type, index) => ({id: index, value: type, name: type}));

const handleChangePhone =
    (setFieldValue: FormikProps<ManagerFormProps>['setFieldValue'], fieldName: string) =>
        (newValue: string | number | null | undefined) => {
            if (newValue) {
                setFieldValue(fieldName, `+${newValue}`);
            }
        };

export const ManagersForm: FC<ManagersFormProps> = ({
                                                        businessId,
                                                        handleClose,
                                                        isEditMode = false,
                                                        userId,
                                                        initialValues,
                                                    }) => {
    const roles = useAssignableTypes();
    const intl = useIntl();
    const [trigger] = usersApi.useCreateManagerMutation();
    const [updateTrigger] = usersApi.useUpdateManagerMutation();
    const dispatch = useAppDispatch();

    return (
        <Formik
            initialValues={initialValues ?? managersInitialValue}
            validationSchema={isEditMode ? managersEditFormSchema : managersFormSchema}
            onSubmit={isEditMode && userId
                ? handleUpdate(updateTrigger, dispatch, handleClose, businessId, userId)
                : handleSubmit(trigger, dispatch, handleClose, businessId)}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                  touched,
                  values,
              }: FormikProps<ManagerFormProps>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Box sx={formSx}>
                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="generalInfo"/>
                            </Typography>
                            <Grid
                                container
                                spacing={2}
                            >
                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    <AMPInput
                                        value={values['firstName']}
                                        type={'firstName'}
                                        label={'firstName'}
                                        input={'firstName'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['firstName'] && Boolean(errors['firstName'])}
                                        helperText={getHelperText('firstName', touched, errors, intl)}
                                    />
                                </Grid>
                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    <AMPInput
                                        value={values['lastName']}
                                        type={'lastName'}
                                        label={'lastName'}
                                        input={'lastName'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['lastName'] && Boolean(errors['lastName'])}
                                        helperText={getHelperText('lastName', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                            {/* The login and the kind are both decided once, at creation */}
                            {!isEditMode && (
                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        <AMPInput
                                            value={values['email']}
                                            type={'email'}
                                            label={'email'}
                                            input={'email'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['email'] && Boolean(errors['email'])}
                                            helperText={getHelperText('email', touched, errors, intl)}
                                        />
                                    </Grid>
                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        <AMPFormSelect
                                            data={roles}
                                            value={values['userType'] as string}
                                            label={'role'}
                                            input={'userType'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['userType'] && Boolean(errors['userType'])}
                                            helperText={getHelperText('role', touched, errors, intl)}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            <AMPFormSelect
                                data={processedLanguages(intl)}
                                value={values['language'] as string}
                                label={'language'}
                                input={'language'}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                hasError={touched['language'] && Boolean(errors['language'])}
                                helperText={getHelperText('language', touched, errors, intl)}
                            />
                            {/* Title and phone exist only on the update, so they appear only here */}
                            {isEditMode && (
                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        <AMPFormSelect
                                            data={titles(intl)}
                                            value={values['title'] as string}
                                            label={'title'}
                                            input={'title'}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        <AMPPhoneNumber
                                            value={values['phoneNumber']}
                                            onChange={handleChangePhone(setFieldValue, 'phoneNumber')}
                                            label={'phoneNumber'}
                                            onBlur={handleBlur}
                                            hasError={touched['phoneNumber'] && Boolean(errors['phoneNumber'])}
                                            helperText={getHelperText('phoneNumber', touched, errors, intl)}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                    </Box>
                    <Box sx={actionsWrapperSx}>
                        <CancelButton
                            text="cancel"
                            handler={handleClose}
                        />
                        <SubmitButton
                            text={isEditMode ? 'update' : 'createManager'}
                            color="primary"
                            isSubmitting={isSubmitting}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
